import { Flex } from 'antd';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/format';
import styles from './statistics.module.scss';
import layout from '@/styles/layout.module.scss';
import Card from '@/components/card/card';
import KpiTile from '@/components/kpi-tile/kpi-tile';
import Meter from '@/components/meter/meter';
import Callout from '@/components/callout/callout';
import HoursChart, { hoursChartColors } from '@/components/hours-chart/hours-chart';
import Legend from '@/components/legend/legend';
import StatTable from '@/components/stat-table/stat-table';
import StatusBadge from '@/components/status-badge/status-badge';
import { Button } from '@/components/button/button';
import { CustomFilters } from '@/components/custom-filters/custom-filters';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useScreenFilters } from '@/hooks/use-screen-filters/use-screen-filters';
import { useCallMetric } from '@/hooks/use-call-metric/use-call-metric';
import { useCallbackQueue } from '@/hooks/use-callback-queue/use-callback-queue';
import { useInsights } from '@/hooks/use-insights/use-insights';
import { DashboardFilters } from '@/services/dashboard-service/dashboard.service.types';
import { MINUTES_SAVED_PER_CASE, rangeKey } from '@/pages/dashboard/dashboard.constants';
import {
  demoHandoffReasons,
  demoKnowledgeGaps,
  demoResidentQuestions,
  showDemoData,
} from '@/demo/demo-data';
import { WEEKDAY_KEYS, WEEKEND_INDEXES, deltaCaption } from './statistics.constants';
import {
  LANGUAGE_OVERFLOW_LABEL,
  WEEKDAY_UNAVAILABLE_KEY,
  asCount,
  buildImpactRows,
  buildStatisticsKpiTiles,
  callbackUrgencyCaption,
  toCallbackRows,
  toLanguageRows,
  toTopicBars,
  toWeekdaySeries,
} from './statistics.utils';

/** Fixed, as the mockup pins it: a weekday mean needs every weekday represented. */
const WEEKDAY_WINDOW = '30d';

export function Statistics() {
  const { t } = useTranslation(['statistics', 'metrics']);
  const navigate = useNavigate();

  // One name for the request and the card both, so neither can be moved without the other.
  const rendersCallbackCard = showDemoData;

  const {
    scopedFilter,
    selectedFilter,
    setPageFilter,
    canQuery,
    setCompanyName,
    defaultCompanyName,
    companies,
    isFetched,
    isLoadingUserData,
    setCompanySearchQuery,
    companySearchData,
    companySearchLoading,
    companySearchError,
  } = useScreenFilters();

  const {
    callMetricData,
    isLoading: isLoadingMetrics,
    error: metricsError,
  } = useCallMetric(scopedFilter, canQuery);
  const {
    insightsData,
    isLoading: isLoadingInsights,
    error: insightsError,
  } = useInsights(scopedFilter, canQuery);
  const {
    callbackQueueData,
    isLoading: isLoadingCallbacks,
    error: callbacksError,
  } = useCallbackQueue(scopedFilter, canQuery, rendersCallbackCard);

  // The weekday card is a 30-day mean whatever the range switcher says, as the mockup
  // draws it, so it needs its own window rather than the page's.
  const {
    callMetricData: weekdayMetricData,
    isLoading: isLoadingWeekday,
    error: weekdayError,
  } = useCallMetric(
    { ...scopedFilter, time_range: WEEKDAY_WINDOW, start_time: undefined, end_time: undefined },
    canQuery
  );

  const activePreset = selectedFilter.time_range ?? 'custom';

  const tiles = buildStatisticsKpiTiles({
    metrics: callMetricData,
    satisfaction: insightsData?.overall_satisfaction,
  });
  const caption = deltaCaption(activePreset);

  const impactRows = buildImpactRows({ metrics: callMetricData });
  const topicBars = toTopicBars(insightsData?.trending_topics ?? {});
  const languageRows = toLanguageRows(insightsData?.language_distribution ?? {});
  const weekday = toWeekdaySeries(weekdayMetricData?.chart ?? []);
  const callbackRows = toCallbackRows(callbackQueueData);
  const callbackCaption = callbackUrgencyCaption(
    callbackQueueData,
    t(`metrics:range.in.${rangeKey(activePreset)}`)
  );

  // While the viewer resolves, `scopedFilter` withholds the company and every query is
  // gated off, so nothing is in flight and nothing has arrived. That counts as loading:
  // a card reading only its own query's flags would render a blank body under its title.
  const isResolvingViewer = isLoadingUserData;

  // Each real card answers for itself, so a failed request reads as an error rather than
  // as a measured zero. Every empty branch is gated on the payload having arrived: a query
  // the gate never sent reports neither loading nor error, and "none in this range" would
  // then be asserted about a window nobody was asked about.
  const topicsMessage = insightsError
    ? t('statistics:topics.messages.error')
    : isResolvingViewer || isLoadingInsights
      ? t('statistics:topics.messages.loading')
      : insightsData && !topicBars.length
        ? t('statistics:topics.messages.empty')
        : null;

  const languagesMessage = insightsError
    ? t('statistics:languages.messages.error')
    : isResolvingViewer || isLoadingInsights
      ? t('statistics:languages.messages.loading')
      : insightsData && !languageRows.length
        ? t('statistics:languages.messages.empty')
        : null;

  const weekdayMessage = weekdayError
    ? t('statistics:weekday.messages.error')
    : isResolvingViewer || isLoadingWeekday
      ? t('statistics:weekday.messages.loading')
      : weekdayMetricData && !weekday.available
        ? t(`statistics:${WEEKDAY_UNAVAILABLE_KEY[weekday.reason]}`)
        : null;

  const callbackMessage = callbacksError
    ? t('statistics:callbacks.messages.error')
    : isResolvingViewer || isLoadingCallbacks
      ? t('statistics:callbacks.messages.loading')
      : callbackQueueData && !callbackRows.length
        ? t('statistics:callbacks.messages.empty')
        : null;

  // The store owns the range, so the three fields are dropped rather than mirrored back.
  const handleCompanyChange = ({
    time_range: _range,
    start_time: _start,
    end_time: _end,
    ...filters
  }: DashboardFilters) => {
    setPageFilter(filters);
    setCompanyName(filters.company_name ?? defaultCompanyName);
  };

  // Grouped so the fabricated data is reached from one place and from nowhere else.
  const demoCards = () => {
    // Against the real maximum, not row one: a reordered list would produce bars past 100%.
    const widestHandoffShare = Math.max(...demoHandoffReasons.map((reason) => reason.share));

    return [
      <Card
        demoBadge="no-feature"
        hint={t('statistics:residentQuestions.hint')}
        key="resident-questions"
        title={t('statistics:residentQuestions.title')}
      >
        <StatTable
          align={['left', 'right']}
          columns={[
            t('statistics:residentQuestions.columns.question'),
            t('statistics:residentQuestions.columns.occurrences'),
          ]}
          rows={demoResidentQuestions.map((entry) => ({
            label: t(entry.question),
            values: [asCount(entry.occurrences)],
          }))}
        />
      </Card>,

      <Card
        demoBadge="not-ported"
        hint={t('statistics:knowledgeGaps.hint')}
        key="knowledge-gaps"
        title={t('statistics:knowledgeGaps.title')}
      >
        <div className={styles.rows}>
          {demoKnowledgeGaps.map((gap) => (
            <div className={styles.gapRow} key={gap.question}>
              <div>
                <div className={styles.gapQuestion}>{`„${t(gap.question)}”`}</div>
                <div className={styles.gapNote}>
                  {t('statistics:knowledgeGaps.note', {
                    occurrences: formatNumber(gap.occurrences),
                    folder: t(gap.folder),
                  })}
                </div>
              </div>
              <StatusBadge label={t('statistics:knowledgeGaps.badge')} variant="hand" />
            </div>
          ))}
        </div>
        <Button
          buttonVariant="secondary"
          className={layout.cta}
          icon={<PlusIcon aria-hidden="true" />}
          onClick={() => navigate('/documents')}
        >
          {t('statistics:knowledgeGaps.action')}
        </Button>
      </Card>,

      <Card
        demoBadge="not-ported"
        hint={t('statistics:handoffReasons.hint')}
        key="handoff-reasons"
        title={t('statistics:handoffReasons.title')}
      >
        <div className={styles.bars}>
          {demoHandoffReasons.map((reason) => (
            <Meter
              caption={t('metrics:units.cases', { count: reason.cases })}
              key={reason.reason}
              label={t(reason.reason)}
              layout="labelled"
              tone="series5"
              value={(reason.share / widestHandoffShare) * 100}
            />
          ))}
        </div>
        <Callout>{t('statistics:handoffReasons.callout')}</Callout>
      </Card>,
    ];
  };

  // Mockup order, wide and narrow rows alternating. Composed from a list rather than
  // fixed rows, so a hidden card repacks the grid instead of half-emptying one.
  const cards = [
    <Card hint={t('statistics:topics.hint')} key="topics" title={t('statistics:topics.title')}>
      {topicsMessage ? (
        <p className={layout.empty}>{topicsMessage}</p>
      ) : (
        <div className={styles.bars}>
          {topicBars.map((bar) => (
            <Meter
              caption={bar.caption}
              key={bar.label}
              label={bar.label}
              layout="labelled"
              value={bar.percent}
            />
          ))}
        </div>
      )}
    </Card>,

    <Card hint={t('statistics:impact.hint')} key="impact" title={t('statistics:impact.title')}>
      {metricsError ? (
        <p className={layout.empty}>{t('statistics:impact.error')}</p>
      ) : (
        // Marked busy on the same terms as the KPI row above: the builder falls back to
        // zeros, and five of them read as a measured quarter.
        <div
          aria-busy={isResolvingViewer || isLoadingMetrics || undefined}
          className={clsx((isResolvingViewer || isLoadingMetrics) && layout.pending)}
        >
          <StatTable
            align={['left', 'right']}
            columns={[t('statistics:impact.columns.measure'), t('statistics:impact.columns.value')]}
            rows={impactRows}
          />
          <Callout>{t('statistics:impact.assumption', { count: MINUTES_SAVED_PER_CASE })}</Callout>
        </div>
      )}
    </Card>,

    <Card
      className={styles.weekdayCard}
      hint={t('statistics:weekday.hint')}
      key="weekday"
      title={t('statistics:weekday.title')}
    >
      {weekdayMessage ? (
        <p className={layout.empty}>{weekdayMessage}</p>
      ) : (
        <>
          <HoursChart
            fill
            label={t('statistics:weekday.title')}
            labels={WEEKDAY_KEYS.map((key) => t(`statistics:weekday.labels.${key}`))}
            offIndexes={WEEKEND_INDEXES}
            showAxis
            values={weekday.available ? weekday.values : []}
          />
          {/* The shading marks calendar weekends, not the tenant's own closed days. */}
          <Legend
            items={[
              { color: hoursChartColors.inHours, label: t('statistics:weekday.legend.weekdays') },
              { color: hoursChartColors.offHours, label: t('statistics:weekday.legend.weekend') },
            ]}
          />
        </>
      )}
    </Card>,

    ...(showDemoData ? demoCards() : []),

    <Card
      hint={t('statistics:languages.hint')}
      key="languages"
      title={t('statistics:languages.title')}
    >
      {languagesMessage ? (
        <p className={layout.empty}>{languagesMessage}</p>
      ) : (
        <div className={styles.rows}>
          {languageRows.map((row) => (
            <Meter
              count={asCount(row.count)}
              countWidth={56}
              key={row.label}
              label={row.label === LANGUAGE_OVERFLOW_LABEL ? t('metrics:other') : row.label}
              labelWidth={76}
              layout="row"
              tone={row.tone}
              value={row.percent}
            />
          ))}
        </div>
      )}
    </Card>,

    // Real data, unlike the three above, but on a card that is still half-built: its
    // department grouping and its wait figure both change meaning before it is finished,
    // so it waits behind the same flag rather than reading as settled.
    ...(rendersCallbackCard
      ? [
          <Card
            hint={t('statistics:callbacks.hint')}
            key="callback"
            title={t('statistics:callbacks.title')}
          >
            {callbackMessage ? (
              <p className={layout.empty}>{callbackMessage}</p>
            ) : (
              <>
                <StatTable
                  align={['left', 'right']}
                  columns={[
                    t('statistics:callbacks.columns.department'),
                    t('statistics:callbacks.columns.cases'),
                  ]}
                  rows={callbackRows}
                />
                {/* The table shows neither the total nor the urgency split, and a queue
                    read without them looks like the whole of the work. */}
                {callbackCaption && <p className={styles.tableNote}>{callbackCaption}</p>}
              </>
            )}
          </Card>,
        ]
      : []),
  ];

  const rows = Array.from({ length: Math.ceil(cards.length / 2) }, (_, row) =>
    cards.slice(row * 2, row * 2 + 2)
  );

  return (
    <Flex className={layout.page} vertical>
      {isFetched && (
        <CustomFilters
          companiesList={companies?.map((company) => company.name) || []}
          filters={selectedFilter}
          defaultCompanyValue={defaultCompanyName}
          onChangeFilters={handleCompanyChange}
          onSearchQueryChange={setCompanySearchQuery}
          searchResults={companySearchData}
          isSearchLoading={companySearchLoading}
          searchError={companySearchError}
        />
      )}

      {/* A failed request otherwise renders as a measured zero across every tile. */}
      {metricsError && <Callout tone="strong">{t('statistics:errors.metrics')}</Callout>}

      {/* Marked busy rather than left asserting zeros as measured fact. */}
      <div
        aria-busy={isResolvingViewer || isLoadingMetrics || undefined}
        className={clsx(layout.kpis, (isResolvingViewer || isLoadingMetrics) && layout.pending)}
      >
        {tiles.map((tile) => (
          <KpiTile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            unit={tile.unit}
            note={tile.note}
            delta={tile.delta}
            deltaCaption={caption}
            variant={tile.variant}
            demoBadge={tile.demoBadge}
          />
        ))}
      </div>

      {/* Row parity picks which column is the wide one, so hiding or adding a card swaps
          the proportions of every row below it. */}
      {rows.map((row, index) => (
        <div className={index % 2 === 0 ? layout.gridWide : layout.gridNarrow} key={index}>
          {row}
        </div>
      ))}
    </Flex>
  );
}
